import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { NotFoundPageComponent } from '@app/pages/not-found-page/not-found-page.component';
import { LoginPageComponent } from '@app/pages/login-page/login-page.component';
import { LogoutPageComponent } from '@app/pages/logout-page/logout-page.component';
import { OldLeadPageComponent } from '@app/pages/old-lead-page/old-lead-page.component';
import { LeadPageComponent } from '@app/pages/lead-page/lead-page.component';
import { LeadWrapperPageComponent } from '@app/pages/lead-wrapper-page/lead-wrapper-page.component';
import { ThankYouPageComponent } from '@app/pages/thank-you-page/thank-you-page.component';
import { PassengersPageComponent } from '@app/pages/passengers-page/passengers-page.component';
import { PortalOrderPageGuard } from '@app/guards/portal-order-page.guard';
import { GratitudePageComponent } from '@app/pages/gratitude-page/gratitude-page.component';
import { OverviewAndPaymentPageComponent } from '@app/pages/overview-and-payment-page/overview-and-payment-page.component';
import { HomePageComponent } from '@app/pages/home-page/home-page.component';
import { OptionPageComponent } from '@app/pages/option-page/option-page.component';
import { BookingPageComponent } from '@app/pages/booking-page/booking-page.component';
import { TripPageComponent } from '@app/pages/trip-page/trip-page.component';
import { AuthGuard } from '@app/guards/auth.guard';
import { AncillaryBundlePageComponent } from '@app/pages/ancillary-bundle-page/ancillary-bundle-page.component';
import { ClientTravelersPageComponent } from '@app/pages/client-travelers-page/client-travelers-page.component';
import { ClientTravelerPageComponent } from '@app/pages/client-traveler-page/client-traveler-page.component';
import { AncillaryUpsellPageComponent } from '@app/pages/ancillary-upsell-page/ancillary-upsell-page.component';
import { CompletePageComponent } from '@app/pages/complete-page/complete-page.component';
import { ErrorPageComponent } from '@app/pages/error-page/error-page.component';
import { DisruptionProtectionPageComponent } from '@app/pages/disruption-protection-page/disruption-protection-page.component';
import { CancelForAnyReasonPageComponent } from '@app/pages/cancel-for-any-reason-page/cancel-for-any-reason-page.component';
import { TiersPageComponent } from '@app/pages/tiers-page/tiers-page.component';
import { TierChangePageComponent } from '@app/pages/tier-change-page/tier-change-page.component';
import { QuoteLayoutPageComponent } from '@app/pages/quote-layout-page/quote-layout-page.component';
import { QuotePageComponent } from '@app/pages/quote-page/quote-page.component';
import { QuoteExpiredPageComponent } from '@app/pages/quote-expired-page/quote-expired-page.component';
import { QuoteThankYouPageComponent } from '@app/pages/quote-thank-you-page/quote-thank-you-page.component';
import { TripsPageComponent } from '@app/pages/trips-page/trips-page.component';

export enum PortalRoutes {
  Home = '',
  Login = 'login',
  Logout = 'logout',
  NotFound = '404',
  Lead = ':lead-id',
  Option = ':option-id',

  DisruptionProtection = 'disruption-protection',
  CancelForAnyReason = 'cancel-for-any-reason',
  Tiers = 'tiers',
  TierChange = 'tier-change',
  AncillaryBundleComponent = 'ancillary-bundle-component',
  Gratuity = 'gratuity',
  OverviewPayment = 'overview-payment',
  Complete = 'complete',
  Error = 'error',

  Trips = 'trips',
  TripsParam = 'trip-id',
  Bookings = 'bookings',
  BookingsParam = 'booking-id',
  Travelers = 'travelers',
  TravelerParam = 'traveler-id',
  AncillaryUpsell = 'upgrade',
  AncillaryUpsellParam = 'trip-portal-link',

  Quote = 'quote',
  QuoteToken = 'token',
  QuoteExpired = 'expired',
  QuoteThankYou = 'thank-you',
}

export enum PortalQueryParams {
  UtmSource = 'utm_source',
  /** Third segment under `/:lead/:option/` (or upsell third segment) after sign-in resume. */
  CheckoutResume = 'checkoutResume',
  /** `option` — path `[lead, option, segment]`; `upsell` — `[upgrade, upsellTrip, segment]`. */
  CheckoutPath = 'checkoutPath',
  UpsellTripPortalLink = 'upsellTrip',
}

const routes: Routes = [
  {
    path: PortalRoutes.Home,
    component: HomePageComponent,
  },
  {
    path: PortalRoutes.Login,
    component: LoginPageComponent,
  },
  {
    path: PortalRoutes.Logout,
    component: LogoutPageComponent,
  },
  {
    path: PortalRoutes.NotFound,
    component: NotFoundPageComponent,
  },
  {
    path: PortalRoutes.Quote,
    component: QuoteLayoutPageComponent,
    children: [
      {
        path: PortalRoutes.QuoteExpired,
        component: QuoteExpiredPageComponent,
      },
      {
        path: PortalRoutes.QuoteThankYou,
        component: QuoteThankYouPageComponent,
      },
      {
        path: `:${PortalRoutes.QuoteToken}`,
        component: QuotePageComponent,
      },
    ],
  },
  {
    path: PortalRoutes.Trips,
    component: TripsPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: PortalRoutes.Trips + `/:${PortalRoutes.TripsParam}`,
    component: TripPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: PortalRoutes.Bookings + `/:${PortalRoutes.BookingsParam}`,
    component: BookingPageComponent,
    canActivate: [AuthGuard],
  },
  {
    path: PortalRoutes.Travelers,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: ClientTravelersPageComponent,
      },
      {
        path: `:${PortalRoutes.TravelerParam}`,
        component: ClientTravelerPageComponent,
      },
    ],
  },
  {
    path: 'l/:lead-id',
    component: OldLeadPageComponent, // simple redirect for the old leads
  },
  {
    path: `ancillary-upsell/:${PortalRoutes.AncillaryUpsellParam}`, // simple redirect for the old upsells
    redirectTo: `${PortalRoutes.AncillaryUpsell}/:${PortalRoutes.AncillaryUpsellParam}`,
    pathMatch: 'full',
  },
  {
    path: PortalRoutes.AncillaryUpsell,
    component: AncillaryUpsellPageComponent,
    children: [
      {
        path: PortalRoutes.Complete,
        component: CompletePageComponent,
      },
      {
        path: PortalRoutes.Error,
        component: ErrorPageComponent,
      },
      {
        path: `:${PortalRoutes.AncillaryUpsellParam}`,
        children: [
          {
            path: PortalRoutes.Tiers,
            component: TiersPageComponent,
            data: {
              ancillaryUpsell: true,
            },
          },
          {
            path: PortalRoutes.AncillaryBundleComponent,
            component: AncillaryBundlePageComponent,
            data: {
              path: PortalRoutes.AncillaryBundleComponent,
              ancillaryUpsell: true,
            },
          },
          {
            path: PortalRoutes.DisruptionProtection,
            component: DisruptionProtectionPageComponent,
            data: {
              ancillaryUpsell: true,
            },
          },
          {
            path: PortalRoutes.CancelForAnyReason,
            component: CancelForAnyReasonPageComponent,
            data: {
              ancillaryUpsell: true,
            },
          },
          {
            path: PortalRoutes.Gratuity,
            component: GratitudePageComponent,
            data: {
              ancillaryUpsell: true,
            },
          },
          {
            path: PortalRoutes.OverviewPayment,
            component: OverviewAndPaymentPageComponent,
            data: {
              path: PortalRoutes.OverviewPayment,
              ancillaryUpsell: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: PortalRoutes.Lead,
    component: LeadWrapperPageComponent,
    children: [
      {
        path: '',
        component: LeadPageComponent,
        data: {
          path: PortalRoutes.Lead,
        },
      },
      {
        path: PortalRoutes.Option,
        canActivateChild: [PortalOrderPageGuard],
        children: [
          {
            path: '',
            component: OptionPageComponent,
            data: {
              path: PortalRoutes.Option,
            },
          },
          {
            path: PortalRoutes.Travelers,
            component: PassengersPageComponent,
            data: {
              path: PortalRoutes.Travelers,
            },
          },
          {
            path: PortalRoutes.DisruptionProtection,
            component: DisruptionProtectionPageComponent,
          },
          {
            path: PortalRoutes.Tiers,
            component: TiersPageComponent,
          },
          {
            path: PortalRoutes.TierChange,
            component: TierChangePageComponent,
          },
          {
            path: PortalRoutes.CancelForAnyReason,
            component: CancelForAnyReasonPageComponent,
          },
          {
            path: PortalRoutes.AncillaryBundleComponent,
            component: AncillaryBundlePageComponent,
            data: {
              path: PortalRoutes.AncillaryBundleComponent,
            },
          },
          {
            path: PortalRoutes.Gratuity,
            component: GratitudePageComponent,
            data: {
              path: PortalRoutes.Gratuity,
            },
          },
          {
            path: PortalRoutes.OverviewPayment,
            component: OverviewAndPaymentPageComponent,
            data: {
              path: PortalRoutes.OverviewPayment,
            },
          },
          {
            path: PortalRoutes.Complete,
            component: ThankYouPageComponent,
            data: {
              path: PortalRoutes.Complete,
            },
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: `/${PortalRoutes.NotFound}`,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { scrollPositionRestoration: 'top' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
